'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, useInView, AnimatePresence } from 'motion/react';
import { SUPPORT_MAILTO } from '@/lib/constants';
import {
  Users,
  MessageSquare,
  BarChart3,
  Zap,
  Shield,
  Globe,
  Kanban,
  Bot,
  Tag,
  FileText,
  Settings,
  ArrowRight,
  Check,
  Sparkles,
  Phone,
  Mail,
  Target,
  TrendingUp,
  Clock,
  Bell,
  Filter,
  Download,
  UserPlus,
  Building2,
  Workflow,
  Brain,
  MessageCircle,
  Send,
  LayoutDashboard,
  PieChart,
  LineChart,
  Activity,
  Lock,
  Users2,
  Briefcase,
  CreditCard,
  Plug,
  ChevronDown,
  Play,
} from 'lucide-react';

/**
 * Feature category type for organized display
 */
interface FeatureCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  glowColor: string;
  image: string;
  features: {
    title: string;
    description: string;
    icon: React.ReactNode;
  }[];
}

/**
 * AI-generated feature images from Nano Banana Pro (Gemini 3 Pro Image)
 * Generated using scripts/generate-feature-images.ts
 */
const FEATURE_IMAGES = {
  leads: '/features/leads-pipeline.jpeg',
  ai: '/features/ai-assistant.jpeg',
  whatsapp: '/features/whatsapp-integration.jpeg',
  clients: '/features/client-management.jpeg',
  automation: '/features/automation-workflows.jpeg',
  team: '/features/team-collaboration.jpeg',
  analytics: '/features/analytics-dashboard.jpeg',
  integrations: '/features/integrations.jpeg',
};

/**
 * All CRM features organized by category
 * Based on comprehensive codebase analysis
 */
const FEATURE_CATEGORIES: FeatureCategory[] = [
  {
    id: 'leads',
    title: 'Lead Pipeline Management',
    description: 'Visual Kanban boards, smart filtering, and AI-powered lead qualification',
    icon: <Kanban className="w-6 h-6" />,
    gradient: 'from-amber-500 to-orange-600',
    glowColor: 'amber',
    image: FEATURE_IMAGES.leads,
    features: [
      {
        title: 'Drag & Drop Kanban',
        description: 'Visual pipeline with customizable stages, colors, and real-time updates',
        icon: <Kanban className="w-5 h-5" />,
      },
      {
        title: 'Smart Filters',
        description: 'Meta-style filtering by tags, dates, assignee, AI scores, and form responses',
        icon: <Filter className="w-5 h-5" />,
      },
      {
        title: 'Bulk Actions',
        description: 'Move, tag, delete, or start AI qualification for multiple leads at once',
        icon: <Users className="w-5 h-5" />,
      },
      {
        title: 'Lead Reminders',
        description: 'Schedule follow-ups with notification alerts',
        icon: <Bell className="w-5 h-5" />,
      },
      {
        title: 'Activity Timeline',
        description: 'Complete history of interactions, status changes, and notes',
        icon: <Activity className="w-5 h-5" />,
      },
      {
        title: 'Import & Export',
        description: 'CSV import with smart field mapping and filtered exports',
        icon: <Download className="w-5 h-5" />,
      },
    ],
  },
  {
    id: 'ai',
    title: 'AI-Powered Automation',
    description: 'Cosmos AI, WhatsApp bot, and intelligent lead qualification',
    icon: <Brain className="w-6 h-6" />,
    gradient: 'from-purple-500 to-indigo-600',
    glowColor: 'purple',
    image: FEATURE_IMAGES.ai,
    features: [
      {
        title: 'Cosmos AI Assistant',
        description: 'Natural language CRM control (search, create, update, analyze, and more)',
        icon: <Bot className="w-5 h-5" />,
      },
      {
        title: 'WhatsApp AI Bot',
        description:
          'Automated lead qualification with personality customization and escalation rules',
        icon: <MessageCircle className="w-5 h-5" />,
      },
      {
        title: 'Visual Flow Builder',
        description: 'N8N-style conversation flows with branches, conditions, and actions',
        icon: <Workflow className="w-5 h-5" />,
      },
      {
        title: 'AI Lead Summaries',
        description: 'Automatic conversation analysis and lead insights generation',
        icon: <Sparkles className="w-5 h-5" />,
      },
      {
        title: 'Smart Auto-Tagging',
        description: 'AI analyzes conversations and automatically applies relevant tags',
        icon: <Tag className="w-5 h-5" />,
      },
      {
        title: 'Operation Rewind',
        description: 'Undo any AI action within 24 hours with full audit trail',
        icon: <Clock className="w-5 h-5" />,
      },
    ],
  },
  {
    id: 'whatsapp',
    title: 'WhatsApp Business Integration',
    description: 'Official API integration with templates, broadcasts, and team inbox',
    icon: <Phone className="w-6 h-6" />,
    gradient: 'from-green-500 to-emerald-600',
    glowColor: 'green',
    image: FEATURE_IMAGES.whatsapp,
    features: [
      {
        title: 'Two-Way Messaging',
        description: 'Send and receive WhatsApp messages directly from the CRM',
        icon: <MessageSquare className="w-5 h-5" />,
      },
      {
        title: 'Template Library',
        description: 'Create, manage, and send Meta-approved message templates',
        icon: <FileText className="w-5 h-5" />,
      },
      {
        title: 'Broadcast Campaigns',
        description: 'Mass messaging with segmentation, scheduling, and analytics',
        icon: <Send className="w-5 h-5" />,
      },
      {
        title: 'Welcome Automation',
        description: 'Automatic personalized messages for new leads from any channel',
        icon: <Zap className="w-5 h-5" />,
      },
      {
        title: 'Shared Team Inbox',
        description: 'Collaborative inbox with assignment, filters, and presence indicators',
        icon: <Users2 className="w-5 h-5" />,
      },
      {
        title: 'Delivery Analytics',
        description: 'Track sent, delivered, read rates, and failed message recovery',
        icon: <BarChart3 className="w-5 h-5" />,
      },
    ],
  },
  {
    id: 'clients',
    title: 'Client Management',
    description: 'Complete client lifecycle from lead conversion to retention',
    icon: <Building2 className="w-6 h-6" />,
    gradient: 'from-blue-500 to-cyan-600',
    glowColor: 'blue',
    image: FEATURE_IMAGES.clients,
    features: [
      {
        title: 'Lead-to-Client Conversion',
        description: 'One-click conversion preserving history and relationships',
        icon: <UserPlus className="w-5 h-5" />,
      },
      {
        title: 'Custom Properties',
        description: 'Notion-style editable cards with configurable field visibility',
        icon: <Settings className="w-5 h-5" />,
      },
      {
        title: 'Financial Tracking',
        description: 'Deal values, billing milestones, payment status and budget tracking',
        icon: <CreditCard className="w-5 h-5" />,
      },
      {
        title: 'Document Management',
        description: 'Upload, organize, and share client documents',
        icon: <FileText className="w-5 h-5" />,
      },
      {
        title: 'Activity Timeline',
        description: 'Complete interaction history with team notes and @mentions',
        icon: <Activity className="w-5 h-5" />,
      },
      {
        title: 'Custom Fields',
        description: 'Create text, number, date, rating, and dropdown fields',
        icon: <Tag className="w-5 h-5" />,
      },
    ],
  },
  {
    id: 'automation',
    title: 'Workflow Automation',
    description: 'Set-and-forget workflows that work while you sleep',
    icon: <Zap className="w-6 h-6" />,
    gradient: 'from-rose-500 to-pink-600',
    glowColor: 'rose',
    image: FEATURE_IMAGES.automation,
    features: [
      {
        title: 'Smart Triggers',
        description:
          'Automatically react when leads arrive, status changes, or conversations start',
        icon: <Target className="w-5 h-5" />,
      },
      {
        title: 'Automatic Follow-ups',
        description:
          'Send WhatsApp messages, update statuses, and add tags without lifting a finger',
        icon: <Send className="w-5 h-5" />,
      },
      {
        title: 'Visual Builder',
        description: 'Drag-and-drop workflow editor, no coding required',
        icon: <Workflow className="w-5 h-5" />,
      },
      {
        title: 'Scheduled Actions',
        description: 'Set delays up to 30 days for perfectly-timed follow-ups',
        icon: <Clock className="w-5 h-5" />,
      },
      {
        title: 'Lead Nurturing',
        description: 'Automatically guide leads through your pipeline with personalized touches',
        icon: <TrendingUp className="w-5 h-5" />,
      },
      {
        title: 'Smart Reminders',
        description: 'Never miss a follow-up with automated reminder creation',
        icon: <Bell className="w-5 h-5" />,
      },
    ],
  },
  {
    id: 'team',
    title: 'Team Collaboration',
    description: 'Hierarchical permissions, departments, and real-time collaboration',
    icon: <Users className="w-6 h-6" />,
    gradient: 'from-teal-500 to-green-600',
    glowColor: 'teal',
    image: FEATURE_IMAGES.team,
    features: [
      {
        title: 'Role-Based Access',
        description: 'Owner, Admin, Manager, Member roles with custom permission sets',
        icon: <Shield className="w-5 h-5" />,
      },
      {
        title: 'Data Scopes',
        description: 'Control access: all data, team data, own data only, or none',
        icon: <Lock className="w-5 h-5" />,
      },
      {
        title: 'Team Invitations',
        description: 'Email invites with subscription-based seat limits',
        icon: <Mail className="w-5 h-5" />,
      },
      {
        title: 'Departments',
        description: 'Organize teams with icons, colors, and member assignments',
        icon: <Briefcase className="w-5 h-5" />,
      },
      {
        title: 'Presence Tracking',
        description: "See who's viewing records in real-time with status indicators",
        icon: <Activity className="w-5 h-5" />,
      },
      {
        title: 'Team Directory',
        description: 'Search and filter members by department, role, or status',
        icon: <Users2 className="w-5 h-5" />,
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics & Reporting',
    description: 'Real-time dashboards, AI metrics, and financial reports',
    icon: <BarChart3 className="w-6 h-6" />,
    gradient: 'from-violet-500 to-purple-600',
    glowColor: 'violet',
    image: FEATURE_IMAGES.analytics,
    features: [
      {
        title: 'Dashboard Widgets',
        description: 'Customizable KPIs with pipeline overview, revenue trends, and AI activity',
        icon: <LayoutDashboard className="w-5 h-5" />,
      },
      {
        title: 'AI Analytics',
        description: 'Conversation metrics, AI vs human ratios, qualification rates, escalations',
        icon: <PieChart className="w-5 h-5" />,
      },
      {
        title: 'Financial Reports',
        description: 'Revenue by period, cost breakdown, profit margins, overdue payments',
        icon: <LineChart className="w-5 h-5" />,
      },
      {
        title: 'FAQ Analytics',
        description: 'Track FAQ usage, unmatched questions, and auto-suggest new FAQs',
        icon: <Activity className="w-5 h-5" />,
      },
      {
        title: 'Broadcast Stats',
        description: 'Delivery rates, read rates, and per-message status tracking',
        icon: <TrendingUp className="w-5 h-5" />,
      },
      {
        title: 'Period Comparison',
        description: 'Compare 7-day, 30-day, 90-day periods with change indicators',
        icon: <BarChart3 className="w-5 h-5" />,
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations Hub',
    description: 'Connect your marketing channels and import leads automatically',
    icon: <Plug className="w-6 h-6" />,
    gradient: 'from-orange-500 to-red-600',
    glowColor: 'orange',
    image: FEATURE_IMAGES.integrations,
    features: [
      {
        title: 'Meta Ads Integration',
        description: 'Auto-import leads from Facebook & Instagram Lead Ads in real-time',
        icon: <Globe className="w-5 h-5" />,
      },
      {
        title: 'TikTok Ads Integration',
        description: 'Capture leads from TikTok Instant Forms automatically',
        icon: <Globe className="w-5 h-5" />,
      },
      {
        title: 'Gmail Import',
        description: 'Email-to-lead rules with form detection (Contact Form 7, Gravity, etc.)',
        icon: <Mail className="w-5 h-5" />,
      },
      {
        title: 'Ad-to-Automation',
        description: 'Trigger workflows instantly when leads arrive from any ad platform',
        icon: <Zap className="w-5 h-5" />,
      },
      {
        title: 'Channel Auto-Tags',
        description: 'Automatic tagging and pipeline assignment per marketing channel',
        icon: <Tag className="w-5 h-5" />,
      },
      {
        title: 'Multi-Channel Dashboard',
        description: 'See all your marketing leads in one unified pipeline',
        icon: <LayoutDashboard className="w-5 h-5" />,
      },
    ],
  },
];

/**
 * Hero statistics for social proof
 */
const HERO_STATS = [
  { value: '9', label: 'Automation Actions' },
  { value: '8+', label: 'Integrations' },
];

/**
 * Animation variants for staggered reveals
 */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut' as const,
    },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut' as const,
    },
  },
};

/**
 * Animated Section Component with viewport detection
 */
function AnimatedSection({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Floating Icon with pulse animation
 */
function FloatingIcon({
  children,
  gradient,
  size = 'md',
}: {
  children: React.ReactNode;
  gradient: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClasses = {
    sm: 'w-10 h-10 rounded-lg',
    md: 'w-12 h-12 rounded-xl',
    lg: 'w-14 h-14 rounded-2xl',
  };

  return (
    <motion.div
      className={`${sizeClasses[size]} bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg relative`}
      whileHover={{ scale: 1.1, rotate: 5 }}
      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
    >
      <motion.div
        className={`absolute inset-0 ${sizeClasses[size]} bg-gradient-to-br ${gradient} blur-xl opacity-50`}
        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.3, 0.5] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
}

/**
 * FeaturesPage Component
 *
 * Comprehensive features showcase with:
 * - Dark theme matching landing page
 * - Framer Motion animations
 * - Scroll-triggered reveals
 * - Beautiful glass morphism
 */
export default function FeaturesPage() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  return (
    <div
      className="min-h-screen text-white overflow-x-hidden"
      style={{ backgroundColor: '#050505' }}
    >
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        {/* Animated nebula glows */}
        <motion.div
          className="absolute -top-[20%] -left-[10%] w-[600px] h-[600px] rounded-full bg-amber-600/20 blur-[120px]"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-purple-900/15 blur-[100px]"
          animate={{
            scale: [1.1, 1, 1.1],
            opacity: [0.15, 0.25, 0.15],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-[40%] right-[20%] w-[400px] h-[400px] rounded-full bg-blue-900/10 blur-[80px]"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative" style={{ zIndex: 1 }}>
        {/* Navigation */}
        <motion.nav
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/10"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-3 group">
              <motion.div
                whileHover={{ rotate: 10 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <Image
                  src="/logo-nobg.png"
                  alt="CMG Workflow"
                  width={40}
                  height={40}
                  className="rounded-lg"
                />
              </motion.div>
              <span className="font-bold text-xl group-hover:text-amber-400 transition-colors">
                CMG Workflow
              </span>
            </Link>
            <div className="flex items-center gap-6">
              <Link
                href="/"
                className="text-white/70 hover:text-white transition-colors relative group"
              >
                Home
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 group-hover:w-full transition-all" />
              </Link>
              <Link href="/features" className="text-amber-500 font-medium relative">
                Features
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-amber-500" />
              </Link>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  href="/auth/login"
                  className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full font-medium hover:shadow-lg hover:shadow-amber-500/25 transition-all"
                >
                  Get Started
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.nav>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6"
            >
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="w-4 h-4 text-amber-500" />
              </motion.div>
              <span className="text-amber-500 text-sm font-medium">Powered by Cosmos AI</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
            >
              Everything You Need to{' '}
              <span className="bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 bg-clip-text text-transparent">
                Scale Your Business
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white/60 max-w-3xl mx-auto mb-12"
            >
              From AI-powered lead qualification to seamless client integrations, visual pipelines
              to team collaboration, CMG Workflow CRM has every tool you need to convert more leads
              and delight customers.
            </motion.p>

            {/* Hero Stats */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="flex flex-wrap justify-center gap-8 md:gap-16 mb-12"
            >
              {/* eslint-disable @eslint-react/no-array-index-key -- static config array (HERO_STATS); never reordered at runtime */}
              {HERO_STATS.map((stat, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.1 }}
                  className="text-center group cursor-default"
                >
                  <motion.div
                    className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent"
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: index * 0.2,
                    }}
                  >
                    {stat.value}
                  </motion.div>
                  <div className="text-white/50 text-sm mt-1 group-hover:text-white/70 transition-colors">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
              {/* eslint-enable @eslint-react/no-array-index-key */}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  href="/auth/register"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-amber-500/30 transition-all group"
                >
                  Start Free Trial
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-5 h-5" />
                  </motion.div>
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  href="/demo"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/10 rounded-full font-semibold text-lg hover:bg-white/10 hover:border-white/20 transition-all"
                >
                  <Play className="w-5 h-5" />
                  Book a Demo
                </Link>
              </motion.div>
            </motion.div>

            {/* Scroll indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-16"
            >
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="flex flex-col items-center gap-2 text-white/40"
              >
                <span className="text-xs uppercase tracking-widest">Explore Features</span>
                <ChevronDown className="w-5 h-5" />
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Feature Categories Grid */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Explore Our{' '}
                <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                  Feature Suite
                </span>
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-lg">
                Click on any category to explore detailed features
              </p>
            </AnimatedSection>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {FEATURE_CATEGORIES.map((category, index) => (
                <motion.button
                  key={category.id}
                  variants={itemVariants}
                  onClick={() =>
                    setActiveCategory(activeCategory === category.id ? null : category.id)
                  }
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`group relative p-6 rounded-2xl border text-left transition-all duration-300 backdrop-blur-sm ${
                    activeCategory === category.id
                      ? 'bg-white/10 border-amber-500/50'
                      : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.07] hover:border-white/20'
                  }`}
                >
                  {/* Gradient glow on hover */}
                  <motion.div
                    className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${category.gradient}`}
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 0.1 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Glass shine effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="relative">
                    <FloatingIcon gradient={category.gradient}>{category.icon}</FloatingIcon>
                    <h3 className="text-lg font-semibold mb-2 mt-4">{category.title}</h3>
                    <p className="text-white/50 text-sm mb-4 line-clamp-2">
                      {category.description}
                    </p>
                    <div className="flex items-center gap-1 text-amber-500 text-sm font-medium">
                      <span>{category.features.length} features</span>
                      <motion.div
                        animate={{ rotate: activeCategory === category.id ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </motion.div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Feature Details (Expanded Category) */}
        <AnimatePresence mode="wait">
          {activeCategory && (
            <motion.section
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="px-6 overflow-hidden"
            >
              <div className="max-w-7xl mx-auto pb-10">
                {FEATURE_CATEGORIES.filter((c) => c.id === activeCategory).map((category) => (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className="bg-white/[0.03] border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm"
                  >
                    {/* AI-Generated Feature Image (Nano Banana Pro) - Fixed sizing */}
                    <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
                      <Image
                        src={category.image}
                        alt={category.title}
                        fill
                        className="object-cover object-center"
                        priority
                        sizes="(max-width: 768px) 100vw, 1200px"
                      />
                      {/* Gradient overlays for better text visibility */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />

                      {/* Category info overlay */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="absolute bottom-0 left-0 right-0 p-8 md:p-12"
                      >
                        <div className="flex items-center gap-4">
                          <FloatingIcon gradient={category.gradient} size="lg">
                            {category.icon}
                          </FloatingIcon>
                          <div>
                            <h3 className="text-2xl md:text-3xl font-bold">{category.title}</h3>
                            <p className="text-white/70 text-lg">{category.description}</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Nano Banana Pro badge */}
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="absolute top-4 right-4 px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-full text-xs text-white/80 flex items-center gap-1.5 border border-white/10"
                      >
                        <Sparkles className="w-3 h-3 text-amber-500" />
                        AI Generated
                      </motion.div>
                    </div>

                    {/* Features grid */}
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className="p-8 md:p-12"
                    >
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* eslint-disable @eslint-react/no-array-index-key -- static config array (FEATURE_CATEGORIES); never reordered at runtime */}
                        {category.features.map((feature, index) => (
                          <motion.div
                            key={index}
                            variants={itemVariants}
                            whileHover={{ scale: 1.02, y: -4 }}
                            className="p-5 bg-white/[0.03] rounded-xl border border-white/10 hover:border-white/20 hover:bg-white/[0.05] transition-all group"
                          >
                            <div className="flex items-start gap-4">
                              <motion.div
                                whileHover={{ rotate: 10 }}
                                className="w-10 h-10 rounded-lg bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center shrink-0 group-hover:from-amber-500/20 group-hover:to-orange-500/20 transition-all"
                              >
                                {feature.icon}
                              </motion.div>
                              <div>
                                <h4 className="font-semibold mb-1 group-hover:text-amber-400 transition-colors">
                                  {feature.title}
                                </h4>
                                <p className="text-white/50 text-sm">{feature.description}</p>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                        {/* eslint-enable @eslint-react/no-array-index-key */}
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* All Features Deep Dive */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <AnimatedSection className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">
                Complete Feature{' '}
                <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
                  Breakdown
                </span>
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-lg">
                A comprehensive look at everything CMG Workflow CRM offers
              </p>
            </AnimatedSection>

            {FEATURE_CATEGORIES.map((category, categoryIndex) => (
              <AnimatedSection
                key={category.id}
                delay={categoryIndex * 0.1}
                className="mb-16 last:mb-0"
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 mb-6"
                >
                  <FloatingIcon gradient={category.gradient} size="sm">
                    {category.icon}
                  </FloatingIcon>
                  <h3 className="text-xl md:text-2xl font-bold">{category.title}</h3>
                </motion.div>

                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-50px' }}
                  className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
                >
                  {/* eslint-disable @eslint-react/no-array-index-key -- static config array (FEATURE_CATEGORIES); never reordered at runtime */}
                  {category.features.map((feature, index) => (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      whileHover={{ scale: 1.02, x: 4 }}
                      className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-transparent hover:border-white/10 transition-all group"
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.3 }}
                        className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shrink-0 mt-0.5"
                      >
                        <Check className="w-3 h-3" />
                      </motion.div>
                      <div>
                        <h4 className="font-medium mb-0.5 group-hover:text-amber-400 transition-colors">
                          {feature.title}
                        </h4>
                        <p className="text-white/50 text-sm">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                  {/* eslint-enable @eslint-react/no-array-index-key */}
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <AnimatedSection className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="relative bg-gradient-to-br from-amber-500/20 to-orange-600/20 border border-amber-500/30 rounded-3xl p-12 overflow-hidden"
            >
              {/* Animated background glow */}
              <motion.div
                className="absolute -top-1/2 -left-1/2 w-full h-full bg-amber-500/30 rounded-full blur-3xl"
                animate={{
                  x: [0, 100, 0],
                  y: [0, 50, 0],
                }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
              />

              <div className="relative z-10">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-3xl md:text-4xl font-bold mb-4"
                >
                  Ready to Transform Your Business?
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-white/60 mb-8 max-w-xl mx-auto text-lg"
                >
                  Clinica de Marketing has used CMG Workflow CRM since September 2025 to automate
                  their sales, qualify leads with AI, and close more deals.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="flex flex-wrap justify-center gap-4"
                >
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      href="/auth/register"
                      className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-amber-500/30 transition-all"
                    >
                      Start Free Trial
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Link
                      href={SUPPORT_MAILTO}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 border border-white/20 rounded-full font-semibold text-lg hover:bg-white/20 transition-all"
                    >
                      Contact Sales
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </AnimatedSection>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="py-12 px-6 border-t border-white/10"
        >
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <Link href="/" className="flex items-center gap-3 group">
              <motion.div whileHover={{ rotate: 10 }}>
                <Image
                  src="/logo-nobg.png"
                  alt="CMG Workflow"
                  width={32}
                  height={32}
                  className="rounded-lg"
                />
              </motion.div>
              <span className="font-semibold group-hover:text-amber-400 transition-colors">
                CMG Workflow
              </span>
            </Link>
            <div className="text-white/50 text-sm">
              {/* eslint-disable-next-line @eslint-react/purity -- FLAG: new Date() in footer JSX; year updates on re-render only; acceptable for a static marketing page footer */}
              © {new Date().getFullYear()} CMG Workflow. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              {['Privacy', 'Terms', 'Contact'].map((item) => (
                <Link
                  key={item}
                  href={`/${item.toLowerCase()}`}
                  prefetch={false}
                  className="text-white/50 hover:text-white text-sm transition-colors relative group"
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 group-hover:w-full transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </motion.footer>
      </div>
    </div>
  );
}
